/**
 * React Query hooks for managing Page related data
 * These hooks provide clean interfaces for fetching, caching, and managing page API data using React Query.
 */

import useApiClients from '@hooks/useApiClients.tsx';
import {
  Deckline,
  GetPageQueryVariables,
  GetPagesByDeckQueryVariables,
  GetPagesByPageNameQueryVariables,
  LockEvent,
  Page,
} from '@services/api/deckManagement/graphql/schema/API';
import {
  getDecklines,
  getLockedPagesForDeck,
  getPage,
  getPagesByDeck,
  getPagesByPageName,
} from '@services/api/deckManagement/graphql/schema/queries';
import { useQuery } from '@tanstack/react-query';
import { entityKeyAsId } from '@utils/deckHelpers';
import { extractGraphqlOperationName } from '@utils/graphqlOperationName';
import {
  createQueryOrMutationKey,
  createStandardQueryOptions,
  StandardMutationOptions,
  StandardQueryOptions,
} from '@utils/reactQueryConfig.ts';
import { useMemo } from 'react';

/**
 * Interface for common page API parameters that rely on PageId
 * @interface CommonPageApiParams
 * @property {string} pageId - The unique identifier for the Page
 * @property {StandardQueryOptions | StandardMutationOptions} options - Additional React Query options
 */
export interface CommonPageApiParams extends Omit<GetPageQueryVariables, 'page_id'> {
  pageId?: string;
  options?: StandardQueryOptions | StandardMutationOptions;
}

// Query keys for Page related caching
export const pageQueryKeys = {
  all: createQueryOrMutationKey('page'),
  page: (pageId: string) => createQueryOrMutationKey(...pageQueryKeys.all, pageId),
  // Pure invalidation prefix for all getPagesByPageName variants (any pageName/deckId). Used to
  // specifically refresh page-grain records — e.g. lock attributes — without touching sibling page
  // queries like decklines, which also are prefixed with 'page' (pageQueryKeys.all).
  pageGrainsAll: () => createQueryOrMutationKey(...pageQueryKeys.all, 'pageGrains'),
  getPagesByPageName: (pageName: string, deckId?: string) =>
    createQueryOrMutationKey(...pageQueryKeys.all, 'pageGrains', pageName, deckId),
  getDecklines: (pageId: string) => createQueryOrMutationKey(...pageQueryKeys.all, 'decklines', pageId),
  getPagesByDeck: (deckId: string) => createQueryOrMutationKey(...pageQueryKeys.all, 'pagesByDeck', deckId),
  lockedPagesForDeck: (deckId: string) => createQueryOrMutationKey(...pageQueryKeys.all, 'lockedPagesForDeck', deckId),
};

/**
 * Hook to fetch a single Page by ID
 * @param pageId - The unique identifier for the page
 * @param options - Additional React Query options
 */
export const useGetPage = ({ pageId, options }: CommonPageApiParams) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: pageQueryKeys.page(pageId || ''),
    queryFn: async (): Promise<Page> => {
      if (!pageId) {
        throw new Error('Page ID is required');
      }

      // Retrieve Page info using the imported query
      const response = await deckManagementClient.graphql({
        query: getPage,
        variables: {
          page_id: pageId,
        },
      });

      if ('data' in response && response.data?.getPage) {
        return response.data.getPage as Page;
      }

      throw new Error('Failed to fetch page or page not found');
    },
    ...createStandardQueryOptions<Page>({
      ...(options as StandardQueryOptions),
      enabled: Boolean(pageId) && ((options as StandardQueryOptions)?.enabled ?? true),
      rumOp: extractGraphqlOperationName(getPage),
    }),
  });
};

/**
 * Hook to fetch every Page in a deck in a single call (getPagesByDeck).
 *
 * Full `Page` payload by default. Pass `pageFields` to request a slimmer selection — the result
 * is then `Partial<Page>` because only the named fields are populated (callers must guard the
 * fields they read). Mirrors the field-selection pattern in useGetVariantUsageSummary.
 *
 * @param deckId - The deck whose pages to fetch (required for the query to run)
 * @param pageFields - Optional subset of Page fields to select; omit for the full payload
 * @param options - Additional React Query options
 */
export const useGetPagesByDeck = ({
  deckId,
  pageFields,
  options,
}: {
  deckId?: string;
  pageFields?: string[];
  options?: StandardQueryOptions;
}) => {
  const { deckManagementClient } = useApiClients();

  // Field selection (user-defined slim list, or the full generated query as default).
  const fieldsToSelect = pageFields?.length ? pageFields.join('\n      ') : null;
  const dynamicQuery = `
    query GetPagesByDeck($userAlias: String, $deck_id: String!) {
      getPagesByDeck(userAlias: $userAlias, deck_id: $deck_id) {
        ${fieldsToSelect}
      }
    }
  `;

  return useQuery({
    // The selection set is part of the key: a slim and a full fetch for the same deck must not
    // collide in the cache and serve each other's shape.
    queryKey: [...pageQueryKeys.getPagesByDeck(deckId || ''), fieldsToSelect],
    queryFn: async (): Promise<Partial<Page>[]> => {
      if (!deckId) {
        throw new Error('Deck ID is required');
      }

      const response = await deckManagementClient.graphql({
        query: fieldsToSelect === null ? getPagesByDeck : dynamicQuery,
        variables: { deck_id: deckId } as GetPagesByDeckQueryVariables,
      });

      if ('data' in response && response.data?.getPagesByDeck) {
        const pages = response.data.getPagesByDeck as Array<Partial<Page> | null>;
        return pages.filter((page): page is Partial<Page> => page !== null);
      }

      return [];
    },
    ...createStandardQueryOptions<Partial<Page>[]>({
      ...options,
      enabled: Boolean(deckId) && (options?.enabled ?? true),
      rumOp: extractGraphqlOperationName(getPagesByDeck),
    }),
  });
};

/**
 * Builds a page_id → layout_family map for every page in a deck using a single getPagesByDeck
 * call (slim selection: pk + layout_family only).
 *
 * Used by the TOC to determine which pages are configured: a page is considered set up when it
 * has an entry here with a non-empty layout_family. Replaces the former per-page getPage fan-out
 * (one request per TOC entry) — the backend reads the whole deck's pages in one response.
 *
 * @param deckId - The deck whose pages to resolve layout families for
 * @returns pageIdToLayoutFamilyMap (only pages with a non-empty layout_family), an isLoading flag,
 *   and an isError flag. isError lets callers distinguish "not configured" (absent from the map)
 *   from "failed to load", so an errored fetch is not misclassified as needing setup.
 */
export const useGetLayoutFamiliesForDeck = (
  deckId?: string,
): { pageIdToLayoutFamilyMap: Map<string, string>; isLoading: boolean; isError: boolean } => {
  const { data: pages, isLoading, isError } = useGetPagesByDeck({ deckId, pageFields: ['pk', 'layout_family'] });

  const pageIdToLayoutFamilyMap = useMemo(() => {
    const map = new Map<string, string>();
    (pages ?? []).forEach((page) => {
      // pk is the id source (entityKeyAsId); skip anything without both pk and a layout_family.
      if (page.pk && page.layout_family) {
        map.set(entityKeyAsId(page.pk), page.layout_family);
      }
    });
    return map;
  }, [pages]);

  return { pageIdToLayoutFamilyMap, isLoading, isError };
};

/**
 * Get Page Granularity Details give a provide unique Page Name
 * @param pageName - The unique Page Name to query
 * @param pageGrain - Optional Page Grain to filter the results
 * @param deckId - Optional Deck ID to filter the results
 * @param options -Additional React Query options
 */
export const useGetPagesByPageName = ({
  page_name: pageName,
  page_grain: pageGrain,
  deck_id: deckId,
  options,
}: CommonPageApiParams & GetPagesByPageNameQueryVariables) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: pageQueryKeys.getPagesByPageName(pageName || '', deckId || ''),
    queryFn: async (): Promise<Page[]> => {
      if (!pageName) {
        throw new Error('Page Name is required');
      }

      // Retrieve Page Granularity into using the imported query
      const response = await deckManagementClient.graphql({
        query: getPagesByPageName,
        variables: {
          page_name: pageName,
          page_grain: pageGrain,
          deck_id: deckId,
        },
      });

      if ('data' in response && response.data?.getPagesByPageName) {
        return response.data.getPagesByPageName as Page[];
      }

      throw new Error('Failed to fetch granularity pages or pages not found');
    },
    ...createStandardQueryOptions<Page[]>({
      ...(options as StandardQueryOptions),
      enabled: Boolean(pageName) && ((options as StandardQueryOptions)?.enabled ?? true),
      rumOp: extractGraphqlOperationName(getPagesByPageName),
    }),
  });
};

/**
 * Hook to fetch Decklines for a given Page ID
 * @param pageId - The unique identifier for the page
 * @param options - Additional React Query options
 */
export const useGetDecklines = ({ pageId, options }: CommonPageApiParams) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: pageQueryKeys.getDecklines(pageId || ''),
    queryFn: async (): Promise<Deckline[]> => {
      if (!pageId) {
        throw new Error('Page ID is required');
      }

      // Retrieve Deckline info for the Page
      const response = await deckManagementClient.graphql({
        query: getDecklines,
        variables: {
          page_id: pageId,
        },
      });

      if ('data' in response && response.data?.getDecklines) {
        return response.data.getDecklines as Deckline[];
      }

      throw new Error('Failed to fetch page decklines or decklines not found');
    },
    ...createStandardQueryOptions<Deckline[]>({
      ...(options as StandardQueryOptions),
      enabled: Boolean(pageId) && ((options as StandardQueryOptions)?.enabled ?? true),
      rumOp: extractGraphqlOperationName(getDecklines),
    }),
  });
};

/**
 * Hook to fetch the logical pages in a deck that currently hold an active lock.
 * Returns one LockEvent per locked page (deduped across grains by the backend). Drives the
 * deck-lock button's disabled state and the "pages locked" banner.
 * @param deckId - The deck to check for locked pages
 * @param options - Additional React Query options
 */
export const useLockedPagesForDeck = ({ deckId, options }: { deckId?: string; options?: StandardQueryOptions }) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: pageQueryKeys.lockedPagesForDeck(deckId || ''),
    queryFn: async (): Promise<LockEvent[]> => {
      if (!deckId) {
        throw new Error('Deck ID is required');
      }

      const response = await deckManagementClient.graphql({
        query: getLockedPagesForDeck,
        variables: {
          deck_id: deckId,
        },
      });

      if ('data' in response && response.data?.getLockedPagesForDeck) {
        return response.data.getLockedPagesForDeck.filter((event): event is LockEvent => event !== null);
      }

      return [];
    },
    ...createStandardQueryOptions<LockEvent[]>({
      ...options,
      enabled: Boolean(deckId) && (options?.enabled ?? true),
      rumOp: extractGraphqlOperationName(getLockedPagesForDeck),
    }),
  });
};
